import { CommonModule } from '@angular/common';
import {
  Component,
  OnDestroy,
  OnInit,
  ViewChild,
  inject,
} from '@angular/core';
import { FullCalendarComponent, FullCalendarModule } from '@fullcalendar/angular';
import type {
  CalendarOptions,
  DateSelectArg,
  EventClickArg,
  EventInput,
} from '@fullcalendar/core';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';
import esLocale from '@fullcalendar/core/locales/es';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { NzModalModule } from 'ng-zorro-antd/modal';
import { NzSpinModule } from 'ng-zorro-antd/spin';
import Swal from 'sweetalert2';
import { NotifyService } from '@intelishs-group/notify-service';
import { Subject, takeUntil } from 'rxjs';
import { environment } from 'src/environments/environment';
import {
  GoogleCalendarService,
  type GoogleCalendarApiEvent,
} from '@services/google-calendar.service';
import { ProspectService } from '@services/prospect-service';
import { StageColorService } from '@services/stage-color-service';
import type { MovAgendaDTO } from '@schemas/prospect';
import type { ActionButtonDef } from '@components/button-actions/button-actions.types';
import { ModalTitle } from '@components/modal-title/modal-title';
import { ProspectModal } from '@components/prospect-modal/prospect-modal';
import { ButtonActions } from '@components/button-actions/button-actions';
import { RescheduleModal, type RescheduleData, type ScheduleModalMode } from '@components/reschedule-modal/reschedule-modal';
import { toLocalISOString } from '@shared/utils/date.utils';
import { STAGE_ACTIONS, RECHAZO_ACTION, ACTION_ADVANCE_MAP } from '@shared/constants/prospect-actions.constants';

@Component({
  selector: 'app-calendar',
  standalone: true,
  imports: [
    CommonModule,
    FullCalendarModule,
    NzButtonModule,
    NzIconModule,
    NzModalModule,
    NzSpinModule,
    ModalTitle,
    ProspectModal,
    ButtonActions,
    RescheduleModal,
  ],
  templateUrl: './calendar.html',
  styleUrl: './calendar.css',
})
export class CalendarComponent implements OnInit, OnDestroy {
  private readonly notifyService = inject(NotifyService);
  readonly googleCal = inject(GoogleCalendarService);
  private readonly prospectService = inject(ProspectService);
  private readonly stageColorService = inject(StageColorService);

  private readonly destroy$ = new Subject<void>();

  @ViewChild(FullCalendarComponent) fc?: FullCalendarComponent;

  readonly calendarId = environment.googleCalendarId;

  private readonly CALENDAR_STAGES = new Set([2, 3, 13, 14, 15]);
  private readonly CALENDAR_DISPLAY_STAGES = new Set([2, 3, 14, 15]);

  /** Google Event IDs que ya están representados por una agenda interna. */
  private linkedGoogleEventIds = new Set<string>();
  /** Mapa eventId → htmlLink para enriquecer el modal de eventos internos. */
  private googleEventLinksMap = new Map<string, string>();

  calendarLoading = true;
  signingIn = false;
  prospectModalVisible = false;
  prospectPrefillDate: Date | null = null;
  infoModalVisible = false;
  rescheduleModalVisible = false;
  scheduleMode: ScheduleModalMode = 'reagendar';
  selectedAgenda: MovAgendaDTO | null = null;
  infoActions: ActionButtonDef[] = [];
  infoEvent: {
    title: string; description: string;
    start: Date | null; end: Date | null;
    stageName?: string; prospectName?: string;
    stageColor?: string; isGoogle: boolean;
    htmlLink?: string;
  } | null = null;

  calendarOptions: CalendarOptions = {
    plugins: [dayGridPlugin, timeGridPlugin, interactionPlugin],
    initialView: 'dayGridMonth',
    locale: esLocale,
    headerToolbar: {
      left: 'prev,next today',
      center: 'title',
      right: 'dayGridMonth,timeGridWeek,timeGridDay',
    },
    editable: false,
    selectable: true,
    selectMirror: true,
    dayMaxEvents: true,
    nowIndicator: true,
    selectAllow: (span) => {
      const now = new Date();
      if (span.allDay) {
        const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
        return span.start >= today;
      }
      return span.start >= now;
    },
    eventSources: [
      {
        id: 'google-oauth',
        events: (info, successCallback, failureCallback) => {
          this.googleCal
            .getEvents(this.calendarId, info.start, info.end)
            .pipe(takeUntil(this.destroy$))
            .subscribe({
              next: (items) => {
              for (const e of items) {
                if (e.id && e.htmlLink) {
                  this.googleEventLinksMap.set(e.id, e.htmlLink);
                }
              }
              const filtered = items.filter(
                (e) => !e.id || !this.linkedGoogleEventIds.has(e.id)
              );
              successCallback(filtered.map((x) => this.mapGoogleToFc(x)));
            },
              error: (err: unknown) => failureCallback(err instanceof Error ? err : new Error(String(err))),
            });
        },
      },
      {
        id: 'agenda-sinay',
        events: async (_info, successCallback, failureCallback) => {
          try {
            const items = await this.preloadAgendaIds();
            const filtered = items.filter((a) =>
              this.CALENDAR_DISPLAY_STAGES.has(a.idEtapa ?? 0)
            );
            successCallback(filtered.map((a) => this.mapAgendaToFc(a)));
          } catch (err) {
            failureCallback(err instanceof Error ? err : new Error(String(err)));
          }
        },
      },
    ],
    loading: (isLoading) => { this.calendarLoading = isLoading; },
    select: (arg: DateSelectArg) => this.onSelectRange(arg),
    eventClick: (arg: EventClickArg) => this.onEventClick(arg),
    eventDidMount: (info) => {
      const color = info.event.extendedProps['stageColor'] as string | undefined;
      if (color) {
        info.el.style.setProperty('--stage-color', color === 'blue' ? 'cornflowerblue' : color);
      }
    },
  };

  constructor() {
    this.googleCal.accessToken$.pipe(takeUntil(this.destroy$)).subscribe(() => {
      queueMicrotask(() => this.refetchEvents());
    });
  }

  async ngOnInit(): Promise<void> {
    await this.stageColorService.ensureLoaded();
    await this.preloadAgendaIds();
    this.refetchEvents();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  async connectGoogle(): Promise<void> {
    this.signingIn = true;
    try {
      await this.googleCal.signIn();
      this.notifyService.successNotyfy('Google', 'Sesión iniciada correctamente');
      this.refetchEvents();
    } catch (e) {
      const msg = e instanceof Error ? e.message : '';
      const cancelled = msg.toLowerCase().includes('popup') || msg.toLowerCase().includes('cancel');
      if (!cancelled) {
        this.notifyService.errorNotyfy(msg || 'Error al conectar con Google');
      }
    } finally {
      this.signingIn = false;
    }
  }

  disconnectGoogle(): void {
    this.googleCal.signOut();
    this.notifyService.successNotyfy('Google', 'Sesión cerrada');
    this.refetchEvents();
  }

  onProspectSaved(): void {
    this.refetchEvents();
  }

  private async preloadAgendaIds(): Promise<MovAgendaDTO[]> {
    const items = await this.prospectService.agenda_All();
    const list = items ?? [];
    this.linkedGoogleEventIds = new Set(
      list.map(a => a.googleEventId).filter((id): id is string => !!id)
    );
    return list;
  }

  private refetchEvents(): void {
    try {
      this.fc?.getApi().refetchEvents();
    } catch {
      /* calendar no montado aún */
    }
  }

  private mapGoogleToFc(e: GoogleCalendarApiEvent): EventInput {
    const allDay = !!(e.start?.date && !e.start?.dateTime);
    if (allDay) {
      const sd = e.start!.date!;
      const ed = e.end?.date ?? sd;
      return {
        id: e.id,
        title: e.summary ?? '(Sin título)',
        start: sd,
        end: ed,
        allDay: true,
        classNames: ['agenda-sinay'],
        display: 'block',
        color: '#722ed1',
        extendedProps: { google: e, stageColor: '#722ed1' },
      };
    }
    const start = e.start?.dateTime;
    const end = e.end?.dateTime ?? start;
    return {
      id: e.id,
      title: e.summary ?? '(Sin título)',
      start: start ?? new Date().toISOString(),
      end: end ?? start ?? new Date().toISOString(),
      allDay: false,
      classNames: ['agenda-sinay'],
      display: 'block',
      color: '#722ed1',
      extendedProps: { google: e, stageColor: '#722ed1' },
    };
  }

  private buildAgendaTitle(a: MovAgendaDTO): string {
    if (a.stageName && a.prospectname) return `${a.stageName}: ${a.prospectname}`;
    if (a.stageName) return a.stageName;
    if (a.prospectname) return a.prospectname;
    return 'Evento agendado';
  }

  private mapAgendaToFc(a: MovAgendaDTO): EventInput {
    const color = this.stageColorService.getBySubId(a.idEtapa ?? 0);
    const startDate = a.fechaAgenda ? new Date(a.fechaAgenda) : new Date();
    const endDate = new Date(startDate.getTime() + 3600000);
    return {
      id: 'agenda-' + a.idagenda,
      title: this.buildAgendaTitle(a),
      start: a.fechaAgenda,
      end: toLocalISOString(endDate),
      allDay: false,
      classNames: ['agenda-sinay'],
      display: 'block',
      color,
      editable: false,
      extendedProps: { agenda: a, stageColor: color },
    };
  }

  private onSelectRange(arg: DateSelectArg): void {
    const now = new Date();
    const selected = arg.start;
    if (selected.toDateString() === now.toDateString() && arg.allDay) {
      const next = new Date(now);
      next.setMinutes(0, 0, 0);
      next.setHours(next.getHours() + 1);
      this.prospectPrefillDate = next;
    } else {
      this.prospectPrefillDate = selected;
    }
    this.prospectModalVisible = true;
    arg.view.calendar.unselect();
  }

  private onEventClick(arg: EventClickArg): void {
    arg.jsEvent.preventDefault();
    const ev = arg.event;
    const google = ev.extendedProps['google'] as GoogleCalendarApiEvent | undefined;
    const agenda = ev.extendedProps['agenda'] as MovAgendaDTO | undefined;
    const rawColor = ev.extendedProps['stageColor'] as string | undefined;
    const stageColor = rawColor === 'blue' ? 'cornflowerblue' : rawColor;

    this.selectedAgenda = agenda ?? null;
    this.infoActions = this.buildInfoActions(agenda);

    let htmlLink = google?.htmlLink ?? undefined;
    if (!htmlLink && agenda?.googleEventId) {
      htmlLink = this.googleEventLinksMap.get(agenda.googleEventId);
    }

    this.infoEvent = {
      title: ev.title,
      description: google?.description ?? agenda?.comentario ?? '',
      start: ev.start,
      end: ev.end,
      stageName: agenda?.stageName ?? undefined,
      prospectName: agenda?.prospectname ?? undefined,
      stageColor,
      isGoogle: !!google,
      htmlLink,
    };
    this.infoModalVisible = true;
  }

  private buildInfoActions(agenda: MovAgendaDTO | undefined): ActionButtonDef[] {
    if (!agenda) return [];
    const paso = agenda.idEtapa ?? 0;
    if (!this.CALENDAR_STAGES.has(paso)) return [];
    return [RECHAZO_ACTION, ...(STAGE_ACTIONS[paso] ?? [])];
  }

  onInfoAction(action: string): void {
    if (!this.selectedAgenda) return;
    switch (action) {
      case 'reagendar':
      case 'reagenda-propuesta':
        this.infoModalVisible = false;
        this.scheduleMode = 'reagendar';
        this.rescheduleModalVisible = true;
        break;
      case 'sesion-propuesta':
        this.infoModalVisible = false;
        this.scheduleMode = 'sesion-propuesta';
        this.rescheduleModalVisible = true;
        break;
      case 'rechazo':
        this.infoModalVisible = false;
        this.confirmRechazo();
        break;
      default:
        this.infoModalVisible = false;
        this.advanceStage(action);
        break;
    }
  }

  private async advanceStage(action: string, smotivo?: string): Promise<void> {
    const idDestinationStage = ACTION_ADVANCE_MAP[action];
    if (!idDestinationStage || !this.selectedAgenda?.idProspecto) return;

    try {
      await this.prospectService.stage_Advance({
        idProspect: this.selectedAgenda.idProspecto,
        idDestinationStage,
        ...(smotivo ? { smotivo } : {}),
      });
      this.notifyService.successNotyfy('Avance exitoso', 'El prospecto ha sido actualizado');
      this.refetchEvents();
    } catch (error) {
      const msg = error instanceof Error ? error.message : 'Error al avanzar el prospecto';
      this.notifyService.errorNotyfy(msg);
    }
  }

  private async confirmRechazo(): Promise<void> {
    const result = await Swal.fire({
      title: '¿Estás seguro de rechazar este prospecto?',
      text: 'Esta acción moverá el prospecto a la etapa de Rechazo',
      icon: 'warning',
      input: 'textarea',
      inputLabel: 'Motivo de rechazo',
      inputPlaceholder: 'Escribe el motivo del rechazo...',
      inputValidator: (value) => {
        if (!value?.trim()) return 'El motivo es obligatorio';
        return null;
      },
      showCancelButton: true,
      confirmButtonText: 'Rechazar',
      cancelButtonText: 'Cancelar',
      allowOutsideClick: false,
      allowEscapeKey: false,
      buttonsStyling: false,
      customClass: {
        confirmButton: 'confirm-btnSweet',
        cancelButton: 'cancel-btnSweet',
        actions: 'swal-actions-spaced',
      },
    });
    if (result.isConfirmed) {
      await this.advanceStage('rechazo', result.value);
    }
  }

  async onScheduled(data: RescheduleData): Promise<void> {
    if (!this.selectedAgenda?.idProspecto) return;

    const fecha = new Date(data.fecha);
    const hora = new Date(data.hora);
    fecha.setHours(hora.getHours(), hora.getMinutes(), 0, 0);

    if (this.scheduleMode === 'sesion-propuesta') {
      try {
        await this.prospectService.createAgenda({
          fkidprospecto: this.selectedAgenda.idProspecto,
          scomentario: data.scomentario,
          ffechaagenda: toLocalISOString(fecha),
          sgoogleEventId: data.sgoogleEventId,
          slink: data.slink,
        });
      } catch (error) {
        const msg = error instanceof Error ? error.message : 'Error al crear la agenda';
        this.notifyService.errorNotyfy(msg);
        return;
      }

      try {
        await this.prospectService.stage_Advance({
          idProspect: this.selectedAgenda.idProspecto,
          idDestinationStage: ACTION_ADVANCE_MAP['sesion-propuesta'],
        });
        this.notifyService.successNotyfy('Sesión agendada', 'El prospecto ha sido avanzado correctamente');
        this.refetchEvents();
      } catch (error) {
        const msg = error instanceof Error ? error.message : 'Error al avanzar el prospecto';
        this.notifyService.errorNotyfy(msg);
      }
    } else {
      if (!this.CALENDAR_DISPLAY_STAGES.has(this.selectedAgenda.idEtapa ?? 0)) {
        this.notifyService.errorNotyfy('No se puede reagendar desde este paso');
        return;
      }

      try {
        await this.prospectService.agenda_Reagendar({
          scomentarioCierre: data.scomentario,
          fkidprospecto: this.selectedAgenda.idProspecto,
          scomentario: data.scomentario,
          ffechaagenda: toLocalISOString(fecha),
          sgoogleEventId: data.sgoogleEventId,
          slink: data.slink,
        });
        this.notifyService.successNotyfy('Reagendado', 'La cita ha sido reagendada correctamente');
        this.refetchEvents();
      } catch (error) {
        const msg = error instanceof Error ? error.message : 'Error al reagendar';
        this.notifyService.errorNotyfy(msg);
      }
    }
  }
}
